import { t } from "i18next";
import { match, P } from "ts-pattern";
import { z } from "zod/v4";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  longTextSchema,
  nonFutureDateTimeSchema,
  valueAndLabelSchemaDeprecated,
  yesNoSchema,
  yesOnlySchema,
  type DetentionDetailsMinimumFields,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import {
  getFirstPlaceOfSafetyArrivalDateTime,
  hasPlaceOfSafetyInIncidentOrFormValues,
  isPoliceLeftTimeOutOfExpectedRange,
} from "../../workflowLogic/calculateDetentionTime.js";
import { getSupportedIncidentTypeSelection } from "../../workflowLogic/helpers/getIncidentTypeSelection.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";

export const formSpecification = new MultiPageFormBuilder()
  .withReadonlyFieldAlwaysVisible({
    fieldName: "endIncidentBanner",
    type: "banner",
    bannerType: "error",
    body: ({ incidentWorkItem }) => ({
      translationKey: tk("preEndIncident.warningBanner"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
  })

  .withFieldConditionallyVisible({
    fieldName: "whyEndingMcaForm",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "startedFormInError" as const,
          "personHadCapacity" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("preEndIncident.whyEndingMcaForm.title"),
    requiredLabel: tk("preEndIncident.whyEndingMcaForm.required"),
    options: [
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.startedFormInError"),
        value: "startedFormInError",
      },
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.personHadCapacity"),
        value: "personHadCapacity",
      },
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => false)
        .with(undefined, () => false)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "whyEndingMcaFormOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    label: tk("preEndIncident.whyEndingMcaFormOther.title"),
    multiline: true,
    rows: "5",
    requiredLabel: tk("preEndIncident.whyEndingMcaFormOther.required"),
    isVisible: ({ formValues }) =>
      formValues.whyEndingMcaForm?.value === "other",
  })

  .withFieldConditionallyVisible({
    fieldName: "endingReason",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "startedFormInError" as const,
          "formExpired" as const,
          "nhsNotEngage" as const,
          "medicalExamination" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk("preEndIncident.endingReason.options.startedFormInError"),
        value: "startedFormInError",
      },
      {
        label: tk("preEndIncident.endingReason.options.formExpired"),
        value: "formExpired",
      },
      {
        label: tk("preEndIncident.endingReason.options.nhsNotEngage"),
        value: "nhsNotEngage",
      },
      {
        label: tk("preEndIncident.endingReason.options.medicalExamination"),
        value: "medicalExamination",
      },
      {
        label: tk("preEndIncident.endingReason.options.other"),
        value: "other",
      },
    ],
    label: tk("preEndIncident.endingReason.title"),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => false)
        .with("136", () => true)
        .with(undefined, () => true)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "additionalEndingReason",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) => formValues.endingReason?.value === "other",
    label: tk("preEndIncident.endingReason.additionalInformation"),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "whyFormExpired",
    type: "multi-select",
    schema: z
      .array(
        valueAndLabelSchemaDeprecated(
          z.enum([
            "noBedAvailable" as const,
            "delayedAssessment" as const,
            "processProblems" as const,
          ]),
        ),
      )
      .min(1, "At least one option must be selected."),
    isVisible: ({ formValues }) =>
      formValues.endingReason?.value === "formExpired",
    label: tk("preEndIncident.whyFormExpired.title"),
    options: [
      {
        label: tk("preEndIncident.whyFormExpired.options.noBedAvailable"),
        value: "noBedAvailable",
      },
      {
        label: tk("preEndIncident.whyFormExpired.options.delayedAssessment"),
        value: "delayedAssessment",
      },
      {
        label: tk("preEndIncident.whyFormExpired.options.processProblems"),
        value: "processProblems",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "otherProcessProblemsReason",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    multiline: true,
    rows: "5",
    isVisible: ({ formValues }) =>
      (formValues.whyFormExpired ?? []).some(
        (option) => option.value === "processProblems",
      ),
    label: tk("preEndIncident.whyFormExpired.otherProcessProblems"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyNhsNotEngage",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "internetConnectivityIssue" as const,
          "emailNotReceived" as const,
          "handoverCompletedVerbally" as const,
          "usedLocalForm" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.endingReason?.value === "nhsNotEngage",
    label: tk("preEndIncident.whyNhsNotEngage.title"),
    options: [
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.internetConnection"),
        value: "internetConnectivityIssue",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.emailNotReceived"),
        value: "emailNotReceived",
      },
      {
        label: tk(
          "preEndIncident.whyNhsNotEngage.options.handoverCompletedVerbally",
        ),
        value: "handoverCompletedVerbally",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.localForm"),
        value: "usedLocalForm",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.other"),
        value: "other",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "additionalInformationNotEngage",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.whyNhsNotEngage?.value === "other",
    label: tk("preEndIncident.whyNhsNotEngage.additionalInformationNotEngage"),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "dateTimeOfDetention",
    type: "date-time",
    timeLabel: tk("detentionDetails.timeOfDetention"),
    dateLabel: tk("detentionDetails.dateOfDetention"),
    disableFuture: true,
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    isVisible: ({ formValues, incidentWorkItem }) => {
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) => form.formType === IncidentFormTypeSchema.enum.section136,
          )
          ?.sections.find(
            (section) => section.id === FormSectionSchema.enum.detentionDetails,
          )?.data ?? (undefined as DetentionDetailsMinimumFields | undefined);

      const hasDateTimeDetentionStated = !!formSectionData?.dateTimeOfDetention;

      if (hasDateTimeDetentionStated) return false;

      return match(formValues)
        .with(
          {
            whyNhsNotEngage: {
              value: P.union(
                "emailNotReceived",
                "internetConnectivityIssue",
                "usedLocalForm",
                "handoverCompletedVerbally",
                "other",
              ),
            },
          },
          () => true,
        )
        .with(
          { endingReason: { value: P.union("other", "medicalExamination") } },
          () => true,
        )
        .with({ whyFormExpired: P.when(Boolean) }, () => true)
        .otherwise(() => false);
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "didNotArrive",
    type: "switch",
    ...new FieldBuilder()
      .withNullableSchema(yesOnlySchema)
      .captureValueLabel()
      .build(),
    label: tk("arrivePlaceOfSafety.didNotArrive"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;

      if (hasArrivedPos) return false;

      return match(formValues)
        .with(
          {
            whyNhsNotEngage: {
              value: P.union(
                "emailNotReceived",
                "internetConnectivityIssue",
                "usedLocalForm",
                "handoverCompletedVerbally",
                "other",
              ),
            },
          },
          () => true,
        )
        .with(
          { endingReason: { value: P.union("other", "medicalExamination") } },
          () => true,
        )
        .with({ whyFormExpired: P.when(Boolean) }, () => true)
        .otherwise(() => false);
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "arrivalDateTime",
    type: "date-time",
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("arrivePlaceOfSafety.arrivalDate"),
    timeLabel: tk("arrivePlaceOfSafety.arrivalTime"),
    disableFuture: true,
    label: tk("preEndIncident.didYouArriveAtPos"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;

      if (hasArrivedPos || formValues.didNotArrive?.value === "yes")
        return false;

      return match(formValues)
        .with(
          {
            whyNhsNotEngage: {
              value: P.union(
                "emailNotReceived",
                "internetConnectivityIssue",
                "usedLocalForm",
                "handoverCompletedVerbally",
                "other",
              ),
            },
          },
          () => true,
        )
        .with(
          { endingReason: { value: P.union("other", "medicalExamination") } },
          () => true,
        )
        .with({ whyFormExpired: P.when(Boolean) }, () => true)
        .otherwise(() => false);
    },
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "advisePhysicallyEnter",
    type: "banner",
    bannerType: "warning",
    isVisible: ({ formValues, incidentWorkItem }) => {
      // Reuse the same logic as arrivalDateTime
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;

      // if arrivalDateTime wouldn’t be visible, don’t show this banner either
      if (hasArrivedPos || formValues.didNotArrive?.value === "yes")
        return false;

      const arrivalDateTimeVisible = match(formValues)
        .with(
          {
            whyNhsNotEngage: {
              value: P.union(
                "emailNotReceived",
                "internetConnectivityIssue",
                "usedLocalForm",
                "handoverCompletedVerbally",
                "other",
              ),
            },
          },
          () => true,
        )
        .with(
          { endingReason: { value: P.union("other", "medicalExamination") } },
          () => true,
        )
        .with({ whyFormExpired: P.when(Boolean) }, () => true)
        .otherwise(() => false);

      if (!arrivalDateTimeVisible) return false;

      // now apply your original didNotArrive logic
      return match(formValues.didNotArrive)
        .with({ valueLabel: P._, value: "yes" }, () => false)
        .with(null, undefined, () => true)
        .otherwise(() => false);
    },
    body: tk("arrivePlaceOfSafety.advisePhysicallyEnter"),
  })
  .withFieldConditionallyVisible({
    fieldName: "wereAskedToRemainAtPosByHealthAmhp",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("preEndIncident.wereAskedToRemainAtPosByHealthAmhp"),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
    isVisible: ({ formValues, incidentWorkItem }) => {
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;
      const hasArrivedManualOverride =
        hasArrivedPos || !!formValues.arrivalDateTime?.value;

      if (!hasArrivedManualOverride) return false;

      return match(formValues)
        .with(
          {
            whyNhsNotEngage: {
              value: P.union(
                "emailNotReceived",
                "internetConnectivityIssue",
                "usedLocalForm",
                "handoverCompletedVerbally",
                "other",
              ),
            },
          },
          () => true,
        )
        .with(
          { endingReason: { value: P.union("other", "medicalExamination") } },
          () => true,
        )
        .with({ whyFormExpired: P.when(Boolean) }, () => true)
        .otherwise(() => false);
    },
  })

  .withFieldConditionallyVisible({
    fieldName: "remainedLongerAtPosDueToHealthAmhpRequest",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("preEndIncident.remainedLongerAtPosDueToHealthAmhpRequest"),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
    isVisible: ({ formValues }) => {
      return formValues.wereAskedToRemainAtPosByHealthAmhp?.value === "yes";
    },
  })

  .withFieldConditionallyVisible({
    fieldName: "leavePos",
    type: "date-time",
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("endIncidentCommon.incidentLeaveDatePos"),
    timeLabel: tk("endIncidentCommon.incidentLeaveTimePos"),
    disableFuture: true,
    isVisible: ({ formValues }) =>
      formValues.wereAskedToRemainAtPosByHealthAmhp?.value === "no" ||
      formValues.didNotArrive?.value === "yes" ||
      !!formValues.remainedLongerAtPosDueToHealthAmhpRequest?.value,
  })

  .withReadonlyFieldConditionallyVisible({
    fieldName: "incidentDuration",
    type: "incident-duration",
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("finishForm.totalPosTime"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    getDateTimeLeft: ({ formValues }) => formValues.leavePos?.value,
    getArrivalAtFirstPlaceOfSafetyDateTime: ({
      formValues,
      incidentWorkItem,
    }) =>
      getFirstPlaceOfSafetyArrivalDateTime(incidentWorkItem) ??
      formValues.arrivalDateTime?.value,
    isVisible: ({ formValues, incidentWorkItem }) => {
      const hasPlaceOfSafety =
        hasPlaceOfSafetyInIncidentOrFormValues(incidentWorkItem);

      if (formValues.didNotArrive?.value === "yes" && !hasPlaceOfSafety) {
        return false;
      }

      return (
        formValues.wereAskedToRemainAtPosByHealthAmhp?.value === "no" ||
        !!formValues.remainedLongerAtPosDueToHealthAmhpRequest?.value
      );
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmDateTimeLeaving",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("finishForm.confirmDateTimeLeaving"),
    requiredLabel: tk("finishForm.confirmDateTimeLeavingRequired"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      if (formValues.leavePos === undefined || formValues.leavePos === null) {
        return false;
      }
      const hasPlaceOfSafety =
        hasPlaceOfSafetyInIncidentOrFormValues(incidentWorkItem);

      if (formValues.didNotArrive?.value === "yes" && !hasPlaceOfSafety) {
        return false;
      }
      if (
        formValues.wereAskedToRemainAtPosByHealthAmhp?.value === "no" ||
        !!formValues.remainedLongerAtPosDueToHealthAmhpRequest?.value
      ) {
        return isPoliceLeftTimeOutOfExpectedRange(
          formValues.leavePos.value,
          incidentWorkItem,
          formValues.arrivalDateTime?.value,
        );
      }
      return false;
    },
  })

  .withFieldConditionallyVisible({
    fieldName: "selectedAssessmentOutcome",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "signpostedToCommunity",
          "informalAdmission",
          "section2Detention",
          "section3Detention",
          "unknownHandover",
          "discharged",
          "admitted",
          "other",
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) => {
      return (
        !!formValues.leavePos?.value &&
        (formValues.endingReason?.value === "nhsNotEngage" ||
          formValues.endingReason?.value == "other")
      );
    },
    label: tk("endIncidentCommon.incidentOutcome.title"),
    options: [
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.signpostedToCommunity",
        ),
        value: "signpostedToCommunity",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.informalAdmission",
        ),
        value: "informalAdmission",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.section2Detention",
        ),
        value: "section2Detention",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.section3Detention",
        ),
        value: "section3Detention",
      },
      {
        label: tk("endIncidentCommon.incidentOutcome.options.unknownHandover"),
        value: "unknownHandover",
      },
    ],
  })
  .withPage(["endIncidentBanner"])
  .withPage(["whyEndingMcaForm", "whyEndingMcaFormOther"])
  .withPage(["endingReason", "additionalEndingReason"])
  .withPage(["whyFormExpired", "otherProcessProblemsReason"])
  .withPage(["whyNhsNotEngage", "additionalInformationNotEngage"])
  .withPage(["dateTimeOfDetention"])
  .withPage(["didNotArrive", "arrivalDateTime", "advisePhysicallyEnter"])
  .withPage(["wereAskedToRemainAtPosByHealthAmhp"])
  .withPage(["remainedLongerAtPosDueToHealthAmhpRequest"])
  .withPage(["leavePos", "incidentDuration", "confirmDateTimeLeaving"])
  .withPage(["selectedAssessmentOutcome"])
  .build({ t });

export type PreEndIncidentFormData = ReturnType<
  typeof formSpecification
>["formType"];
